
import React from 'react';
import { Card } from "@/components/ui/card";
import { TrendingUp, Zap } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Label } from 'recharts';
import type { PerformanceData } from './types';

interface LearningVelocityChartProps {
  performanceData: PerformanceData[];
}

export const LearningVelocityChart: React.FC<LearningVelocityChartProps> = ({ performanceData }) => {
  const velocityData = React.useMemo(() => {
    if (performanceData.length < 2) return { data: [], velocityPerWeek: 0, isPositive: true };

    // Sort data by date
    const sortedData = [...performanceData].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    
    // Group scores by week
    const weeklyData = sortedData.reduce((acc, curr) => {
      const date = new Date(curr.created_at);
      const weekNumber = Math.floor(date.getTime() / (7 * 24 * 60 * 60 * 1000));
      const weekStartDate = new Date(weekNumber * 7 * 24 * 60 * 60 * 1000);
      const weekLabel = `W${acc.length + 1}`;
      
      if (!acc.some(item => item.weekNumber === weekNumber)) {
        acc.push({
          weekNumber,
          week: weekLabel,
          date: weekStartDate.toLocaleDateString(),
          scores: [curr.score],
          avgScore: curr.score,
          velocity: 0
        });
      } else {
        const index = acc.findIndex(item => item.weekNumber === weekNumber);
        acc[index].scores.push(curr.score);
        acc[index].avgScore = acc[index].scores.reduce((sum, score) => sum + score, 0) / acc[index].scores.length;
      }
      
      return acc;
    }, [] as Array<{
      weekNumber: number;
      week: string;
      date: string;
      scores: number[];
      avgScore: number;
      velocity: number;
    }>);
    
    // Calculate velocity (change in score between periods)
    for (let i = 1; i < weeklyData.length; i++) {
      weeklyData[i].velocity = weeklyData[i].avgScore - weeklyData[i-1].avgScore;
    }
    
    // Calculate average velocity per week
    let totalVelocity = 0;
    let velocityPoints = 0;
    
    for (let i = 1; i < weeklyData.length; i++) {
      totalVelocity += weeklyData[i].velocity;
      velocityPoints++;
    }
    
    const velocityPerWeek = velocityPoints > 0 ? totalVelocity / velocityPoints : 0;
    const isPositive = velocityPerWeek >= 0;
    
    return { data: weeklyData, velocityPerWeek, isPositive };
  }, [performanceData]);
  
  if (velocityData.data.length < 2) {
    return (
      <Card className="p-6 bg-gradient-to-br from-violet-50 to-white border border-slate-100 shadow-sm">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-500">
          <Zap className="w-5 h-5 text-purple-500" />
          Learning Velocity
        </h2>
        <div className="flex flex-col items-center justify-center h-[300px] text-center">
          <p className="text-gray-500">Not enough data to calculate learning velocity.</p>
          <p className="text-gray-400 text-sm mt-2">Complete more quizzes across multiple weeks to see your improvement rate.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-violet-50 to-white border border-slate-100 shadow-sm">
      <div className="flex items-start justify-between mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-500">
          <Zap className="w-5 h-5 text-purple-500" />
          Learning Velocity
        </h2>
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
          velocityData.isPositive ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
        }`}>
          <TrendingUp className={`w-4 h-4 ${
            velocityData.isPositive ? 'text-green-500' : 'text-amber-500'
          }`} />
          <span className="text-sm font-medium">
            {velocityData.velocityPerWeek.toFixed(1)}% per week
          </span>
        </div>
      </div>
      
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={velocityData.data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <defs>
              <linearGradient id="velocityGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#C4B5FD" stopOpacity={0.3} />
              </linearGradient>
              <linearGradient id="velocityLine" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#8B5CF6" />
                <stop offset="100%" stopColor="#6D28D9" />
              </linearGradient>
            </defs>
            
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
            <XAxis 
              dataKey="week" 
              stroke="#6B7280"
              tick={{ fill: '#6B7280', fontSize: 12 }}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis 
              stroke="#6B7280"
              tick={{ fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
              label={{ 
                value: 'Average Score (%)', 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: '#6B7280', fontWeight: 500 }
              }}
            />
            
            <Tooltip
              formatter={(value: number, name: string) => {
                if (name === 'avgScore') return [`${value.toFixed(1)}%`, 'Average Score'];
                if (name === 'velocity') return [`${value > 0 ? '+' : ''}${value.toFixed(1)}%`, 'Change'];
                return [value, name];
              }}
              labelFormatter={(label) => `Week ${label.substring(1)}`}
              contentStyle={{ 
                backgroundColor: 'white', 
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #E5E7EB'
              }}
            />
            
            <ReferenceLine
              y={0}
              ifOverflow="extendDomain"
              label={false}
              stroke="#CBD5E1"
              strokeDasharray="3 3"
            />

            <Line
              type="monotone"
              dataKey="avgScore"
              name="Score"
              stroke="url(#velocityLine)"
              strokeWidth={2}
              dot={{ 
                fill: "#8B5CF6", 
                strokeWidth: 2,
                stroke: "#FFF",
                r: 4
              }}
              activeDot={{ 
                fill: "#6D28D9", 
                stroke: "#FFF",
                strokeWidth: 2,
                r: 6
              }}
            />
            
            <Line
              type="monotone"
              dataKey="velocity"
              name="Velocity"
              stroke={velocityData.isPositive ? "#10B981" : "#F97316"}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ 
                fill: velocityData.isPositive ? "#10B981" : "#F97316", 
                r: 3
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 text-sm text-gray-600">
        <p className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-violet-600"></span>
          <span>Solid purple line: Average score per week</span>
        </p>
        <p className="flex items-center gap-1.5 mt-1">
          <span className={`w-3 h-3 rounded-full ${velocityData.isPositive ? 'bg-green-500' : 'bg-orange-500'}`}></span>
          <span>Dashed {velocityData.isPositive ? 'green' : 'orange'} line: Score change from previous week (velocity)</span>
        </p>
        <p className="mt-2">
          <strong>Learning Velocity:</strong> {Math.abs(velocityData.velocityPerWeek).toFixed(1)}% 
          {velocityData.isPositive ? ' improvement' : ' decrease'} per week on average
        </p>
      </div>
    </Card>
  );
};
